import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

import { useAnalytics } from './useAnalytics';

// Attributes in-app landings that arrive from the weekly invoice summary
// email. The email's deep links carry `utm_source=weekly_invoice_email`; the
// email itself can't run JS, so this in-app landing is the click-through
// signal. Fires `weekly_invoice_email_visited` once per landing, then strips
// the attribution params so a copied/bookmarked URL doesn't mis-attribute a
// later non-email visit to the campaign (the per-mount ref only guards
// re-fires within one session). The spending filters in the URL
// (`date_stamped_start`, `invoice_kind`, …) are deliberately left intact.
// Mounted once at the app shell.
export function useEmailLinkTracking(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const { track } = useAnalytics();
  const trackedEmailLanding = useRef(false);

  const emailUtmSource = searchParams.get('utm_source');

  useEffect(() => {
    if (emailUtmSource !== 'weekly_invoice_email') {
      trackedEmailLanding.current = false;
      return;
    }
    if (trackedEmailLanding.current) return;
    trackedEmailLanding.current = true;

    const invoiceKind = searchParams.get('invoice_kind');
    track('weekly_invoice_email_visited', {
      link_kind: invoiceKind === 'invoice' || invoiceKind === 'proposal' ? invoiceKind : undefined,
    });

    setSearchParams(
      (params) => {
        params.delete('utm_source');
        params.delete('utm_medium');
        params.delete('utm_campaign');
        return params;
      },
      { replace: true },
    );
  }, [emailUtmSource, searchParams, setSearchParams, track]);
}
